import Layer from '@arcgis/core/layers/Layer';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import WebMap from '@arcgis/core/WebMap';
import { arcGisGetOrCreatePopupTemplate } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisGetOrCreatePopupTemplate';
import {
  createPortalItem,
  createServiceUrlLayer,
  ensureFeatureLayerOutFields,
  type ArcGisAuthOptions,
} from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/externalLayerLoader';
import { isFeedUri } from 'components/MapPage/CorporateViewTabs/EventsTab/externalFeeds';
import { createFeedLayer } from './feedLayer';

type ExternalLayerSyncItem = Pick<
  ExternalLayer,
  | 'id'
  | 'arcgisItemId'
  | 'serviceUrl'
  | 'itemType'
  | 'incident'
  | 'authType'
  | 'defaultVisible'
>;

function isWebMapPortalType(portalType: string | null | undefined): boolean {
  const type = portalType?.toLowerCase() ?? '';
  return type === 'web map' || type === 'webmap';
}

/**
 * Web Maps contain geometries + label styling + popups. Feature / Map / Image
 * Services are portal layers, not Web Maps — decide from the loaded portal item
 * type, not the stored catalog itemType (often stale).
 */
export async function syncExternalLayers(
  externalLayer: ExternalLayerSyncItem & Partial<Pick<ExternalLayer, 'name'>>,
  options: ArcGisAuthOptions
) {
  if (isFeedUri(externalLayer.serviceUrl)) {
    try {
      const layer = await createFeedLayer(externalLayer);
      return { layers: [layer], loadError: false };
    } catch (e) {
      console.warn('Failed to load data feed layer', { externalLayer }, e);
      return { layers: [], loadError: true };
    }
  }

  if (externalLayer.arcgisItemId && options) {
    try {
      const portalItem = await createPortalItem({
        portalItemId: externalLayer.arcgisItemId,
        options,
      });
      if (!portalItem.loaded) {
        await portalItem.load();
      }

      if (isWebMapPortalType(portalItem.type)) {
        try {
          const wm = new WebMap({ portalItem });
          await wm.load();
          await wm.loadAll();
          const wmLayers = wm.layers.toArray();
          wmLayers.forEach((layer) => ensureFeatureLayerOutFields(layer));
          await arcGisGetOrCreatePopupTemplate(
            wmLayers,
            externalLayer.incident?.name
          );
          return { layers: wmLayers, loadError: false };
        } catch {
          console.warn('Failed to load WebMap from PortalItem()', {
            externalLayer,
          });
        }
      }

      const l = await Layer.fromPortalItem({ portalItem });
      await l.load();
      ensureFeatureLayerOutFields(l);
      await arcGisGetOrCreatePopupTemplate([l], externalLayer.incident?.name);
      return { layers: [l], loadError: false };
    } catch (e) {
      console.warn(
        'Failed to load layer fromPortalItem()',
        {
          externalLayer,
        },
        e
      );
    }
  }

  if (externalLayer.serviceUrl) {
    try {
      const layer = await createServiceUrlLayer({
        serviceUrl: externalLayer.serviceUrl,
        options,
      });
      ensureFeatureLayerOutFields(layer);
      await arcGisGetOrCreatePopupTemplate(
        [layer],
        externalLayer.incident?.name
      );
      return { layers: [layer], loadError: false };
    } catch (e) {
      console.warn(
        'Failed to load layer fromArcGISServerUrl()',
        {
          externalLayer,
        },
        e
      );
    }
  }

  return { layers: [], loadError: true };
}
